/**
 * La comptabilité du mode `?demo` (ARTO-58) : douze mois de recettes fictives
 * par service, une grille de répartition réglable, des clôtures qui se font et
 * se défont, pour vérifier l'écran sans API. Les montants et les pourcentages
 * n'existent QUE ici (comme `demoSaisies.ts`) ; l'écran, lui, n'en écrit aucun.
 *
 * Les règles de l'API que l'écran affiche sont rejouées avec les mêmes codes et
 * les mêmes motifs (`docs/dev/api.md` § 12.12) : somme des parts, version
 * existante, mois clôturé, clôture impossible, mois non terminé, élévation du
 * second facteur. Le calcul est celui de l'API : TVA ligne par ligne, HT réparti
 * au plus fort reste.
 */
import {
  encaissementCreationSchema,
  encaissementMiseAJourSchema,
  estServiceDeBase,
  montantsRecette,
  repartirRecette,
  SERVICES_COMPTABLES,
  typeRecetteCreationSchema,
  typeRecetteMiseAJourSchema,
  type ContexteRepartition,
  type RepartitionRecette,
} from '@artosera/shared';
import type {
  Comptabilite,
  ComptabiliteAssocie,
  ComptabiliteDirection,
  CorpsSaisie,
  Encaissement,
  EncaissementsDuMois,
  LibelleTypeRecette,
  MoisComptable,
  TypeRecetteId,
  TypeRecetteVue,
  VersionGrille,
} from '../types.js';
import { ApiError } from './api.js';
import { roleDemo } from './demoData.js';
import { demoSaisies, exigerElevationDemo } from './demoSaisies.js';

const POINTS = 10_000;
const TAUX_NORMAL = 2_000;

/** Un bénéficiaire du partage qui est aussi le compte associé de la démonstration. */
const BENEFICIAIRE_ASSOCIE = 'demo-beneficiaire-1';

interface LigneDemo {
  id: string;
  libelle: string;
  /** `null` : à classer. */
  service: TypeRecetteId | null;
  /** `null` : à saisir. */
  ht: number | null;
  taux: number;
  /** Le TTC d'un encaissement saisi TTC : sa TVA est TTC − HT. */
  ttc?: number | null;
}

interface MoisDemo {
  mois: string;
  lignes: LigneDemo[];
}

// ── Le temps : douze mois qui finissent au mois courant ─────────────────

function moisCourant(): string {
  const parties = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(new Date());
  const valeur = (type: string): string => parties.find((p) => p.type === type)?.value ?? '';
  return `${valeur('year')}-${valeur('month')}`;
}

function decaler(mois: string, n: number): string {
  const [annee, numero] = mois.split('-').map(Number) as [number, number];
  const date = new Date(Date.UTC(annee, numero - 1 + n, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

// ── Les recettes ────────────────────────────────────────────────────────

/** Une suite pseudo-aléatoire fixe : la démonstration est la même à chaque lancement. */
function graine(valeur: number): () => number {
  let s = valeur;
  return () => {
    s = (s * 1_664_525 + 1_013_904_223) % 4_294_967_296;
    return s / 4_294_967_296;
  };
}

function lettre(n: number): string {
  return `Galerie ${String.fromCharCode(65 + (n % 26))}`;
}

function construireMois(mois: string, rang: number, total: number): MoisDemo {
  const hasard = graine(900 + rang * 77);
  const lignes: LigneDemo[] = [];
  const ligne = (
    suffixe: string,
    libelle: string,
    service: TypeRecetteId | null,
    ht: number | null,
  ): void => {
    lignes.push({ id: `${mois}-${suffixe}`, libelle, service, ht, taux: TAUX_NORMAL });
  };
  const essentiel = 5 + Math.floor(rang * 0.6);
  const pro = 3 + Math.floor(rang * 0.4);
  const signature = 1 + Math.floor(rang / 4);
  ligne(
    'a1',
    `Abonnements Essentiel · ${String(essentiel)} galeries`,
    'abonnement',
    essentiel * 14_900,
  );
  ligne('a2', `Abonnements Pro · ${String(pro)} galeries`, 'abonnement', pro * 24_900);
  ligne(
    'a3',
    `Abonnements Signature · ${String(signature)} galeries`,
    'abonnement',
    signature * 39_900,
  );
  const courant = rang === total - 1;
  if (courant) {
    // Le mois courant : de quoi voir ce qui se saisit et ce qui se classe.
    ligne('i0', 'Intégration · Galerie M', 'integration', 234_000);
    ligne('i1', 'Intégration · Galerie N', 'integration', null);
    ligne('m0', 'Migration · Galerie P', 'migration', null);
    ligne('s0', 'Création de site · Galerie Q', 'site_galerie', 480_000);
    ligne('x0', 'Virement reçu · Galerie R', null, 148_000);
  } else {
    const nIntegrations = hasard() < 0.3 ? 2 : hasard() < 0.8 ? 1 : 0;
    for (let i = 0; i < nIntegrations; i += 1) {
      ligne(
        `i${String(i)}`,
        `Intégration · ${lettre(rang * 5 + i)}`,
        'integration',
        Math.round((1_200 + hasard() * 2_400) / 10) * 1_000,
      );
    }
    if (hasard() < 0.55) {
      ligne(
        'm0',
        `Migration · ${lettre(rang * 5 + 3)}`,
        'migration',
        Math.round((600 + hasard() * 1_800) / 10) * 1_000,
      );
    }
    if (hasard() < 0.38) {
      ligne(
        's0',
        `Création de site · ${lettre(rang * 5 + 4)}`,
        'site_galerie',
        Math.round((3_200 + hasard() * 2_600) / 10) * 1_000,
      );
    }
  }
  if (rang >= total - 2) {
    // Les add-ons n'ont pas encore de grille : le mois précédent ne se clôt pas
    // tant que la direction n'en a pas réglé une.
    ligne('d1', `Add-on Statistiques de visite · ${lettre(rang + 1)}`, 'add_on', 6_900);
    ligne('d2', `Add-on Site multilingue · ${lettre(rang + 4)}`, 'add_on', 12_000);
  }
  return { mois, lignes };
}

// ── L'état en mémoire ───────────────────────────────────────────────────

interface Cloture {
  resultat: MoisComptable;
  /** La répartition de chaque encaissement du mois, figée avec lui. */
  encaissements?: Map<string, RepartitionRecette>;
}

/** Un type de recette créé de la démonstration. */
interface TypeDemo {
  id: string;
  libelle: LibelleTypeRecette;
  ordre: number;
  actif: boolean;
  objectifHtCentimes: number | null;
}

interface Etat {
  courant: string;
  mois: MoisDemo[];
  versions: VersionGrille[];
  clotures: Map<string, Cloture>;
  compteur: number;
  types: TypeDemo[];
  encaissements: Encaissement[];
}

let etat: Etat | null = null;

function partsDe(repartition: ReadonlyArray<readonly [string, number]>): VersionGrille['parts'] {
  const libelles = new Map(demoSaisies.listerBeneficiaires().map((b) => [b.id, b.libelle]));
  return repartition.map(([beneficiaireId, partPointsDeBase]) => ({
    beneficiaireId,
    libelle: libelles.get(beneficiaireId) ?? beneficiaireId,
    partPointsDeBase,
  }));
}

function version(
  etatCourant: Etat,
  service: TypeRecetteId,
  depuisMois: string,
  repartition: ReadonlyArray<readonly [string, number]>,
): VersionGrille {
  etatCourant.compteur += 1;
  return {
    id: `demo-version-${String(etatCourant.compteur)}`,
    service,
    depuisMois,
    parts: partsDe(repartition),
    moisClotures: [],
    createdAt: '2026-09-01T08:00:00.000Z',
    updatedAt: '2026-09-01T08:00:00.000Z',
  };
}

function lEtat(): Etat {
  if (etat) return etat;
  const courant = moisCourant();
  const total = 12;
  const mois = Array.from({ length: total }, (_, rang) =>
    construireMois(decaler(courant, rang - (total - 1)), rang, total),
  );
  const nouvel: Etat = {
    courant,
    mois,
    versions: [],
    clotures: new Map(),
    compteur: 0,
    types: [],
    encaissements: [],
  };
  const debut = mois[0]!.mois;
  const milieu = mois[6]!.mois;
  const B1 = 'demo-beneficiaire-1';
  const B2 = 'demo-beneficiaire-2';
  const B3 = 'demo-beneficiaire-3';
  nouvel.versions = [
    version(nouvel, 'abonnement', debut, [
      [B1, 2_000],
      [B2, 1_500],
      [B3, 6_500],
    ]),
    version(nouvel, 'abonnement', milieu, [
      [B1, 2_000],
      [B2, 2_000],
      [B3, 6_000],
    ]),
    version(nouvel, 'integration', debut, [
      [B1, 2_000],
      [B2, 1_000],
      [B3, 7_000],
    ]),
    version(nouvel, 'migration', debut, [
      [B2, 500],
      [B3, 9_500],
    ]),
    version(nouvel, 'site_galerie', debut, [
      [B2, 3_000],
      [B3, 7_000],
    ]),
  ];
  // Un type créé par la direction : une contribution versée en plusieurs
  // fois, avec son objectif, sa grille à partir du mois courant, et un
  // premier encaissement.
  const contribution: TypeDemo = {
    // Un identifiant de 24 caractères hexadécimaux, comme ceux de l'API :
    // les schémas partagés n'en admettent pas d'autre.
    id: 'de0000000000000000000001',
    libelle: { fr: 'Contribution partenaire', en: 'Partner contribution' },
    ordre: 0,
    actif: true,
    objectifHtCentimes: 1_800_000,
  };
  nouvel.types = [contribution];
  nouvel.versions.push(
    version(nouvel, contribution.id, courant, [
      [B2, 5_000],
      [B3, 5_000],
    ]),
  );
  etat = nouvel;
  nouvel.encaissements = [
    encaissementDemo(nouvel, 'demo-encaissement-1', {
      encaisseLe: `${courant}-02`,
      saisie: 'ht',
      montantCentimes: 150_000,
      tauxTvaPointsDeBase: TAUX_NORMAL,
      service: contribution.id,
      payeur: 'Partenaire de démonstration',
      galerieId: null,
      libelle: 'Contribution — MVP',
    }),
  ];
  // Les dix premiers mois sont déjà clôturés, figés avec la grille d'alors.
  for (const m of mois.slice(0, 10)) {
    nouvel.clotures.set(m.mois, {
      resultat: figer(calculer(nouvel, m, true), m.mois),
      encaissements: figerEncaissementsDemo(nouvel, m.mois),
    });
  }
  return nouvel;
}

function figer(mois: MoisComptable, cle: string): MoisComptable {
  const jour = decaler(cle, 1);
  return { ...mois, statut: 'cloture', clotureLe: `${jour}-05T09:30:00.000Z` };
}

// ── Le calcul : celui de l'API ──────────────────────────────────────────

function tva(ht: number, taux: number): number {
  return Math.round((ht * taux) / POINTS);
}

/** La TVA d'une ligne : TTC − HT pour un encaissement saisi TTC, sinon sur le HT. */
function tvaLigne(ligne: LigneDemo): number {
  if (ligne.ttc != null && ligne.ht !== null) return ligne.ttc - ligne.ht;
  return tva(ligne.ht ?? 0, ligne.taux);
}

/** Les encaissements d'un mois, comme des lignes de recette. */
function lignesEncaissees(etatCourant: Etat, mois: string): LigneDemo[] {
  return etatCourant.encaissements
    .filter((e) => e.mois === mois)
    .map((e) => ({
      id: e.id,
      libelle: e.libelle,
      service: e.service,
      ht: e.htCentimes,
      taux: e.tauxTvaPointsDeBase,
      ttc: e.saisie === 'ttc' ? e.ttcCentimes : null,
    }));
}

/** HT réparti au plus fort reste : la somme retombe exactement sur le HT. */
function repartir(ht: number, parts: readonly number[]): number[] {
  const produits = parts.map((p) => ht * p);
  const plancher = produits.map((p) => Math.floor(p / POINTS));
  let reste = ht - plancher.reduce((somme, m) => somme + m, 0);
  const ordre = produits
    .map((p, rang) => ({ rang, reste: p % POINTS }))
    .sort((a, b) => b.reste - a.reste || a.rang - b.rang);
  for (const { rang } of ordre) {
    if (reste === 0) break;
    plancher[rang] = (plancher[rang] ?? 0) + 1;
    reste -= 1;
  }
  return plancher;
}

function versionEn(etatCourant: Etat, service: TypeRecetteId, mois: string): VersionGrille | null {
  let trouvee: VersionGrille | null = null;
  for (const v of etatCourant.versions) {
    if (v.service === service && v.depuisMois <= mois) {
      if (trouvee === null || v.depuisMois > trouvee.depuisMois) trouvee = v;
    }
  }
  return trouvee;
}

function calculer(etatCourant: Etat, mois: MoisDemo, ignorerCloture = false): MoisComptable {
  if (!ignorerCloture) {
    const cloture = etatCourant.clotures.get(mois.mois);
    if (cloture) return cloture.resultat;
  }
  const rangs = new Map(demoSaisies.listerBeneficiaires().map((b, i) => [b.id, i]));
  const libelles = new Map(demoSaisies.listerBeneficiaires().map((b) => [b.id, b.libelle]));
  const parBeneficiaire = new Map<string, number>();
  const toutes = [...mois.lignes, ...lignesEncaissees(etatCourant, mois.mois)];
  const crees = etatCourant.types
    .filter((type) => toutes.some((l) => l.service === type.id))
    .map((type) => type.id);
  const services = [...SERVICES_COMPTABLES, ...crees].map((service) => {
    const lignes = toutes.filter((l) => l.service === service);
    const ht = lignes.reduce((somme, l) => somme + (l.ht ?? 0), 0);
    const tvaService = lignes.reduce((somme, l) => somme + tvaLigne(l), 0);
    const regle = versionEn(etatCourant, service, mois.mois);
    const montants = regle
      ? repartir(
          ht,
          regle.parts.map((p) => p.partPointsDeBase),
        )
      : [];
    const parts = regle
      ? regle.parts.map((p, i) => {
          const montant = montants[i] ?? 0;
          parBeneficiaire.set(
            p.beneficiaireId,
            (parBeneficiaire.get(p.beneficiaireId) ?? 0) + montant,
          );
          return {
            beneficiaireId: p.beneficiaireId,
            libelle: p.libelle,
            partPointsDeBase: p.partPointsDeBase,
            montantCentimes: montant,
          };
        })
      : [];
    return {
      service,
      htCentimes: ht,
      tvaCentimes: tvaService,
      ttcCentimes: ht + tvaService,
      lignes: lignes.length,
      aSaisir: lignes.filter((l) => l.ht === null).length,
      regle: regle ? { id: regle.id, depuisMois: regle.depuisMois } : null,
      parts,
    };
  });
  const aClasserLignes = toutes.filter((l) => l.service === null);
  const aClasserHt = aClasserLignes.reduce((somme, l) => somme + (l.ht ?? 0), 0);
  const aClasserTva = aClasserLignes.reduce((somme, l) => somme + tvaLigne(l), 0);
  const somme = (cle: 'htCentimes' | 'tvaCentimes' | 'ttcCentimes'): number =>
    services.reduce((total, s) => total + s[cle], 0);
  return {
    mois: mois.mois,
    statut: 'provisoire',
    clotureLe: null,
    services,
    totaux: {
      htCentimes: somme('htCentimes'),
      tvaCentimes: somme('tvaCentimes'),
      ttcCentimes: somme('ttcCentimes'),
      lignes: services.reduce((total, s) => total + s.lignes, 0),
      aSaisir: services.reduce((total, s) => total + s.aSaisir, 0),
    },
    aClasser: {
      lignes: aClasserLignes.length,
      aSaisir: aClasserLignes.filter((l) => l.ht === null).length,
      htCentimes: aClasserHt,
      tvaCentimes: aClasserTva,
      ttcCentimes: aClasserHt + aClasserTva,
    },
    nonReparti: services.filter((s) => s.htCentimes > 0 && s.regle === null).map((s) => s.service),
    parBeneficiaire: [...parBeneficiaire.entries()]
      .sort(([a], [b]) => (rangs.get(a) ?? 99) - (rangs.get(b) ?? 99))
      .map(([beneficiaireId, montantCentimes]) => ({
        beneficiaireId,
        libelle: libelles.get(beneficiaireId) ?? beneficiaireId,
        montantCentimes,
      })),
  };
}

// ── Les lectures ────────────────────────────────────────────────────────

function direction(etatCourant: Etat): ComptabiliteDirection {
  const mois = etatCourant.mois.map((m) => calculer(etatCourant, m));
  const cites: string[] = [];
  for (const m of mois) {
    for (const s of m.services) if (!cites.includes(s.service)) cites.push(s.service);
  }
  const parService = cites.map((service) => {
    const lignes = mois.flatMap((m) => m.services.filter((s) => s.service === service));
    return {
      service,
      htCentimes: lignes.reduce((t, s) => t + s.htCentimes, 0),
      tvaCentimes: lignes.reduce((t, s) => t + s.tvaCentimes, 0),
      ttcCentimes: lignes.reduce((t, s) => t + s.ttcCentimes, 0),
    };
  });
  const cumul = new Map<string, { libelle: string; montantCentimes: number }>();
  for (const m of mois) {
    for (const b of m.parBeneficiaire) {
      const ligne = cumul.get(b.beneficiaireId) ?? { libelle: b.libelle, montantCentimes: 0 };
      ligne.montantCentimes += b.montantCentimes;
      cumul.set(b.beneficiaireId, ligne);
    }
  }
  const rangs = new Map(demoSaisies.listerBeneficiaires().map((b, i) => [b.id, i]));
  return {
    vue: 'direction',
    genereeLe: new Date().toISOString(),
    de: mois[0]!.mois,
    a: mois[mois.length - 1]!.mois,
    mois,
    totaux: {
      htCentimes: parService.reduce((t, s) => t + s.htCentimes, 0),
      tvaCentimes: parService.reduce((t, s) => t + s.tvaCentimes, 0),
      ttcCentimes: parService.reduce((t, s) => t + s.ttcCentimes, 0),
      aSaisir: mois.reduce((t, m) => t + m.totaux.aSaisir + m.aClasser.aSaisir, 0),
      aClasser: mois.reduce((t, m) => t + m.aClasser.lignes, 0),
      parService,
      parBeneficiaire: [...cumul.entries()]
        .sort(([a], [b]) => (rangs.get(a) ?? 99) - (rangs.get(b) ?? 99))
        .map(([beneficiaireId, ligne]) => ({ beneficiaireId, ...ligne })),
    },
    typesRecette: etatCourant.types
      .filter((type) => cites.includes(type.id))
      .map((type) => ({ id: type.id, libelle: { ...type.libelle } })),
  };
}

/** Ce que voit l'associé : le HT par service et SA part, jamais celle des autres. */
function associe(etatCourant: Etat): ComptabiliteAssocie {
  const vue = direction(etatCourant);
  const mois = vue.mois.map((m) => {
    const services = m.services.map((s) => {
      const mesParts = s.parts
        .filter((p) => p.beneficiaireId === BENEFICIAIRE_ASSOCIE)
        .map((p) => ({
          libelle: p.libelle,
          partPointsDeBase: p.partPointsDeBase,
          montantCentimes: p.montantCentimes,
        }));
      return {
        service: s.service,
        htCentimes: s.htCentimes,
        aSaisir: s.aSaisir,
        reparti: s.regle !== null,
        mesParts,
      };
    });
    return {
      mois: m.mois,
      statut: m.statut,
      services,
      totalHtCentimes: m.totaux.htCentimes,
      aSaisir: m.totaux.aSaisir + m.aClasser.aSaisir,
      aClasser: m.aClasser.lignes,
      monTotalCentimes: services.reduce(
        (t, s) => t + s.mesParts.reduce((somme, p) => somme + p.montantCentimes, 0),
        0,
      ),
    };
  });
  return {
    vue: 'associe',
    genereeLe: vue.genereeLe,
    de: vue.de,
    a: vue.a,
    mois,
    totaux: {
      htCentimes: mois.reduce((t, m) => t + m.totalHtCentimes, 0),
      monTotalCentimes: mois.reduce((t, m) => t + m.monTotalCentimes, 0),
    },
    typesRecette: vue.typesRecette.map((type) => ({ id: type.id, libelle: { ...type.libelle } })),
  };
}

// ── Types de recette et encaissements ───────────────────────────────────

/** Le jour de Paris d'une date saisie. */
function jourDeParis(valeur: string | Date): string {
  if (typeof valeur === 'string') return valeur;
  const parties = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(valeur);
  const v = (type: string): string => parties.find((p) => p.type === type)?.value ?? '';
  return `${v('year')}-${v('month')}-${v('day')}`;
}

/** La grille et le partage de la démonstration, tels que la répartition partagée les lit. */
function contexteDemo(etatCourant: Etat, mois: string): ContexteRepartition {
  return {
    mois,
    regles: etatCourant.versions.map((v) => ({
      id: v.id,
      service: v.service,
      depuisMois: v.depuisMois,
      parts: v.parts.map((p) => ({
        beneficiaireId: p.beneficiaireId,
        partPointsDeBase: p.partPointsDeBase,
      })),
    })),
    beneficiaires: demoSaisies.listerBeneficiaires().map((b) => ({
      id: b.id,
      libelle: b.libelle,
      recoitLeReste: b.recoitLeReste,
      galeriesExclues: b.galeriesExclues,
    })),
  };
}

function nomType(etatCourant: Etat, service: string): string {
  if (estServiceDeBase(service)) return service;
  return etatCourant.types.find((t) => t.id === service)?.libelle.fr ?? service;
}

interface SaisieEncaissement {
  encaisseLe: string | Date;
  saisie: 'ht' | 'ttc';
  montantCentimes: number;
  tauxTvaPointsDeBase: number;
  service: string;
  payeur: string | null;
  galerieId: string | null;
  libelle: string | null;
}

/** Un encaissement de la démonstration, calculé comme l'API le calcule. */
function encaissementDemo(etatCourant: Etat, id: string, saisie: SaisieEncaissement): Encaissement {
  const jour = jourDeParis(saisie.encaisseLe);
  const mois = jour.slice(0, 7);
  const montants = montantsRecette(
    saisie.saisie,
    saisie.montantCentimes,
    saisie.tauxTvaPointsDeBase,
  );
  const maintenant = new Date().toISOString();
  return {
    id,
    encaisseLe: jour,
    mois,
    service: saisie.service,
    payeur: saisie.payeur,
    galerieId: saisie.galerieId,
    libelle: saisie.libelle ?? nomType(etatCourant, saisie.service),
    saisie: saisie.saisie,
    htCentimes: montants.htCentimes,
    tauxTvaPointsDeBase: saisie.tauxTvaPointsDeBase,
    tvaCentimes: montants.tvaCentimes,
    ttcCentimes: montants.ttcCentimes,
    repartition: repartirRecette(
      { service: saisie.service, htCentimes: montants.htCentimes, galerieId: saisie.galerieId },
      contexteDemo(etatCourant, mois),
    ),
    etatRepartition: 'provisoire',
    createdAt: maintenant,
    updatedAt: maintenant,
  };
}

function invalide(): ApiError {
  return new ApiError(400, 'validation.invalide', 'Les données envoyées sont invalides.', {});
}

function exigerTypeDemo(etatCourant: Etat, service: string, actif: boolean): void {
  if (estServiceDeBase(service)) return;
  const type = etatCourant.types.find((t) => t.id === service);
  if (!type) {
    throw new ApiError(
      422,
      'comptabilite.repartition_invalide',
      'Ce type de recette n’existe pas.',
      {
        raison: 'type_inconnu',
      },
    );
  }
  if (actif && !type.actif) {
    throw new ApiError(
      422,
      'comptabilite.repartition_invalide',
      `« ${type.libelle.fr} » est archivé : rétablissez-le pour y encaisser une somme.`,
      { raison: 'type_archive' },
    );
  }
}

function moisOuverts(etatCourant: Etat, mois: string[]): void {
  refuserSiCloture([...new Set(mois)].filter((m) => etatCourant.clotures.has(m)).sort());
}

/**
 * Une lecture : au mois ouvert, la répartition suit la grille d'aujourd'hui ;
 * au mois clôturé, c'est celle que la clôture a figée, comme l'API.
 */
function relire(etatCourant: Etat, e: Encaissement): Encaissement {
  const cloture = etatCourant.clotures.get(e.mois);
  if (cloture) {
    const figee = cloture.encaissements?.get(e.id);
    return figee === undefined
      ? { ...e, etatRepartition: 'figee_non_detaillee', repartition: null }
      : { ...e, etatRepartition: 'figee', repartition: figee };
  }
  return {
    ...e,
    etatRepartition: 'provisoire',
    repartition: repartirRecette(
      { service: e.service, htCentimes: e.htCentimes, galerieId: e.galerieId },
      contexteDemo(etatCourant, e.mois),
    ),
  };
}

/** La répartition de chaque encaissement d'un mois, à figer avec sa clôture. */
function figerEncaissementsDemo(etatCourant: Etat, mois: string): Map<string, RepartitionRecette> {
  return new Map(
    etatCourant.encaissements
      .filter((e) => e.mois === mois)
      .map((e) => [
        e.id,
        repartirRecette(
          { service: e.service, htCentimes: e.htCentimes, galerieId: e.galerieId },
          contexteDemo(etatCourant, mois),
        ),
      ]),
  );
}

function vuesTypesDemo(etatCourant: Etat): TypeRecetteVue[] {
  const base: TypeRecetteVue[] = SERVICES_COMPTABLES.map((service, rang) => ({
    id: service,
    base: true,
    libelle: null,
    ordre: rang,
    actif: true,
    objectifHtCentimes: null,
    encaisseHtCentimes: null,
    utilise: true,
  }));
  return [
    ...base,
    ...[...etatCourant.types]
      .sort((a, b) => a.ordre - b.ordre)
      .map((type) => ({
        id: type.id,
        base: false,
        libelle: { ...type.libelle },
        ordre: type.ordre,
        actif: type.actif,
        objectifHtCentimes: type.objectifHtCentimes,
        encaisseHtCentimes: etatCourant.encaissements
          .filter((e) => e.service === type.id)
          .reduce((t, e) => t + e.htCentimes, 0),
        utilise:
          etatCourant.encaissements.some((e) => e.service === type.id) ||
          etatCourant.versions.some((v) => v.service === type.id),
      })),
  ];
}

const typeExistant = (): ApiError =>
  conflit('type_existant', 'Un type de recette porte déjà ce nom : renommez l’un des deux.');

// ── Les écritures : mêmes refus que l'API ───────────────────────────────

function conflit(raison: string, message: string, details: Record<string, unknown> = {}): ApiError {
  return new ApiError(409, 'conflit.version', message, { raison, ...details });
}

function verifierParts(
  parts: ReadonlyArray<{ beneficiaireId: string; partPointsDeBase: number }>,
): void {
  const connus = new Set(demoSaisies.listerBeneficiaires().map((b) => b.id));
  const vus = new Set<string>();
  for (const part of parts) {
    if (vus.has(part.beneficiaireId)) {
      throw new ApiError(
        422,
        'comptabilite.repartition_invalide',
        'Un bénéficiaire figure deux fois dans cette version : regroupez ses parts.',
        { raison: 'beneficiaire_en_double', beneficiaireId: part.beneficiaireId },
      );
    }
    vus.add(part.beneficiaireId);
    if (!connus.has(part.beneficiaireId)) {
      throw new ApiError(
        422,
        'comptabilite.repartition_invalide',
        'Ce bénéficiaire n’existe pas dans le partage.',
        { raison: 'beneficiaire_inconnu', beneficiaireId: part.beneficiaireId },
      );
    }
  }
  const somme = parts.reduce((t, p) => t + p.partPointsDeBase, 0);
  if (somme !== POINTS) {
    throw new ApiError(
      422,
      'comptabilite.repartition_invalide',
      `Les parts somment ${String(somme / 100).replace('.', ',')} % : elles doivent faire exactement 100 %.`,
      { raison: 'somme_parts', somme },
    );
  }
}

/** Les mois clôturés dont la version applicable changerait si on remplaçait `avant` par `apres`. */
function moisTouches(
  etatCourant: Etat,
  service: TypeRecetteId,
  avant: VersionGrille[],
  apres: VersionGrille[],
): string[] {
  const choisir = (liste: VersionGrille[], mois: string): VersionGrille | null => {
    let trouvee: VersionGrille | null = null;
    for (const v of liste) {
      if (v.service === service && v.depuisMois <= mois) {
        if (trouvee === null || v.depuisMois > trouvee.depuisMois) trouvee = v;
      }
    }
    return trouvee;
  };
  const empreinte = (v: VersionGrille | null): string =>
    v === null ? 'aucune' : JSON.stringify([v.id, v.depuisMois, v.parts]);
  return [...etatCourant.clotures.keys()]
    .filter((mois) => empreinte(choisir(avant, mois)) !== empreinte(choisir(apres, mois)))
    .sort();
}

function refuserSiCloture(mois: string[]): void {
  if (mois.length === 0) return;
  throw conflit(
    'mois_cloture',
    mois.length > 1
      ? `Les mois ${mois.join(', ')} sont clôturés : rouvrez-les avant d’y toucher.`
      : `Le mois ${mois[0] ?? ''} est clôturé : rouvrez-le avant d’y toucher.`,
    { mois },
  );
}

export const demoComptabilite = {
  lire(): Comptabilite {
    const courant = lEtat();
    return roleDemo() === 'venio_associe' ? associe(courant) : direction(courant);
  },

  listerVersions(): VersionGrille[] {
    return lEtat().versions.map((v) => ({
      ...v,
      parts: v.parts.map((p) => ({ ...p })),
      moisClotures: [...lEtat().clotures.keys()]
        .filter((mois) => versionEn(lEtat(), v.service, mois)?.id === v.id)
        .sort(),
    }));
  },

  creerVersion(corps: {
    service: TypeRecetteId;
    depuisMois: string;
    parts: Array<{ beneficiaireId: string; partPointsDeBase: number }>;
  }): VersionGrille {
    const courant = lEtat();
    verifierParts(corps.parts);
    if (
      courant.versions.some((v) => v.service === corps.service && v.depuisMois === corps.depuisMois)
    ) {
      throw conflit(
        'version_existante',
        'Une version de ce service commence déjà ce mois-là : corrigez-la plutôt que d’en créer une seconde.',
      );
    }
    const creee = version(
      courant,
      corps.service,
      corps.depuisMois,
      corps.parts.map((p) => [p.beneficiaireId, p.partPointsDeBase] as const),
    );
    refuserSiCloture(
      moisTouches(courant, corps.service, courant.versions, [...courant.versions, creee]),
    );
    courant.versions.push(creee);
    return creee;
  },

  modifierVersion(
    id: string,
    corps: {
      depuisMois?: string;
      parts?: Array<{ beneficiaireId: string; partPointsDeBase: number }>;
    },
  ): VersionGrille {
    const courant = lEtat();
    const existante = courant.versions.find((v) => v.id === id);
    if (!existante)
      throw new ApiError(404, 'ressource.introuvable', 'Version de la grille introuvable.');
    if (corps.parts) verifierParts(corps.parts);
    const modifiee: VersionGrille = {
      ...existante,
      depuisMois: corps.depuisMois ?? existante.depuisMois,
      parts: corps.parts
        ? partsDe(corps.parts.map((p) => [p.beneficiaireId, p.partPointsDeBase] as const))
        : existante.parts,
    };
    const apres = courant.versions.map((v) => (v.id === id ? modifiee : v));
    refuserSiCloture(moisTouches(courant, existante.service, courant.versions, apres));
    courant.versions = apres;
    return modifiee;
  },

  cloturer(mois: string): MoisComptable {
    exigerElevationDemo();
    const courant = lEtat();
    const source = courant.mois.find((m) => m.mois === mois);
    if (!source) throw new ApiError(404, 'ressource.introuvable', `Mois ${mois} introuvable.`);
    if (mois >= courant.courant) {
      throw conflit(
        'mois_non_termine',
        `Le mois ${mois} n’est pas terminé : il se clôture à partir du premier jour du mois suivant.`,
        { mois },
      );
    }
    if (courant.clotures.has(mois)) {
      throw conflit('mois_deja_cloture', `Le mois ${mois} est déjà clôturé.`, { mois });
    }
    const calcule = calculer(courant, source);
    const aSaisir = calcule.totaux.aSaisir + calcule.aClasser.aSaisir;
    if (aSaisir > 0 || calcule.aClasser.lignes > 0 || calcule.nonReparti.length > 0) {
      throw conflit(
        'cloture_impossible',
        'Le mois n’est pas complet : saisissez les montants, classez les recettes et donnez une grille à chaque service qui a des recettes.',
        { aSaisir, aClasser: calcule.aClasser.lignes, nonReparti: calcule.nonReparti },
      );
    }
    const fige = { ...calcule, statut: 'cloture' as const, clotureLe: new Date().toISOString() };
    courant.clotures.set(mois, {
      resultat: fige,
      encaissements: figerEncaissementsDemo(courant, mois),
    });
    return fige;
  },

  rouvrir(mois: string): void {
    exigerElevationDemo();
    const courant = lEtat();
    if (!courant.clotures.delete(mois)) {
      throw new ApiError(404, 'ressource.introuvable', `Aucune clôture en vigueur pour ${mois}.`);
    }
  },

  listerTypes(): TypeRecetteVue[] {
    return vuesTypesDemo(lEtat());
  },

  creerType(corps: CorpsSaisie): TypeRecetteVue {
    const courant = lEtat();
    const lu = typeRecetteCreationSchema.safeParse(corps);
    if (!lu.success) throw invalide();
    const nom = lu.data.libelle.fr.toLocaleLowerCase('fr');
    if (courant.types.some((t) => t.libelle.fr.toLocaleLowerCase('fr') === nom)) {
      throw typeExistant();
    }
    courant.compteur += 1;
    const type: TypeDemo = {
      id: `de${String(courant.compteur).padStart(22, '0')}`,
      libelle: lu.data.libelle,
      ordre: lu.data.ordre,
      actif: true,
      objectifHtCentimes: lu.data.objectifHtCentimes,
    };
    courant.types.push(type);
    return vuesTypesDemo(courant).find((t) => t.id === type.id)!;
  },

  modifierType(id: string, corps: CorpsSaisie): TypeRecetteVue {
    const courant = lEtat();
    if (estServiceDeBase(id)) {
      throw conflit(
        'service_de_base',
        'Les cinq services de base ne se renomment, ne s’archivent ni ne se suppriment.',
      );
    }
    const type = courant.types.find((t) => t.id === id);
    if (!type) throw new ApiError(404, 'ressource.introuvable', 'Type de recette introuvable.');
    const lu = typeRecetteMiseAJourSchema.safeParse(corps);
    if (!lu.success) throw invalide();
    if (lu.data.libelle !== undefined) {
      const nom = lu.data.libelle.fr.toLocaleLowerCase('fr');
      if (courant.types.some((t) => t.id !== id && t.libelle.fr.toLocaleLowerCase('fr') === nom)) {
        throw typeExistant();
      }
      type.libelle = lu.data.libelle;
    }
    if (lu.data.ordre !== undefined) type.ordre = lu.data.ordre;
    if (lu.data.actif !== undefined) type.actif = lu.data.actif;
    if (lu.data.objectifHtCentimes !== undefined) {
      type.objectifHtCentimes = lu.data.objectifHtCentimes;
    }
    return vuesTypesDemo(courant).find((t) => t.id === id)!;
  },

  supprimerType(id: string): void {
    const courant = lEtat();
    const vue = vuesTypesDemo(courant).find((t) => t.id === id);
    if (!vue || vue.base) {
      throw new ApiError(404, 'ressource.introuvable', 'Type de recette introuvable.');
    }
    if (vue.utilise) {
      throw conflit(
        'type_utilise',
        'Ce type de recette a servi : archivez-le plutôt que de le supprimer.',
      );
    }
    courant.types = courant.types.filter((t) => t.id !== id);
  },

  listerEncaissements(mois: string): EncaissementsDuMois {
    const courant = lEtat();
    const cloture = courant.clotures.get(mois);
    return {
      mois,
      statut: cloture ? 'cloture' : 'provisoire',
      clotureLe: cloture?.resultat.clotureLe ?? null,
      items: courant.encaissements
        .filter((e) => e.mois === mois)
        .sort((a, b) => a.encaisseLe.localeCompare(b.encaisseLe))
        .map((e) => relire(courant, e)),
    };
  },

  creerEncaissement(corps: CorpsSaisie): Encaissement {
    const courant = lEtat();
    const lu = encaissementCreationSchema.safeParse(corps);
    if (!lu.success) throw invalide();
    const jour = jourDeParis(lu.data.encaisseLe);
    moisOuverts(courant, [jour.slice(0, 7)]);
    exigerTypeDemo(courant, lu.data.service, true);
    courant.compteur += 1;
    const cree = encaissementDemo(courant, `demo-encaissement-${String(courant.compteur)}`, {
      ...lu.data,
      encaisseLe: jour,
    });
    courant.encaissements.push(cree);
    return cree;
  },

  modifierEncaissement(id: string, corps: CorpsSaisie): Encaissement {
    const courant = lEtat();
    const avant = courant.encaissements.find((e) => e.id === id);
    if (!avant) throw new ApiError(404, 'ressource.introuvable', 'Encaissement introuvable.');
    const lu = encaissementMiseAJourSchema.safeParse(corps);
    if (!lu.success) throw invalide();
    const c = lu.data;
    const saisie = c.saisie ?? avant.saisie;
    // Sans montant : le TTC connu vers une saisie TTC, le HT connu sinon.
    const montantCentimes =
      c.montantCentimes ?? (saisie === 'ttc' ? avant.ttcCentimes : avant.htCentimes);
    const service = c.service ?? avant.service;
    exigerTypeDemo(courant, service, service !== avant.service);
    const apres = encaissementDemo(courant, id, {
      encaisseLe: c.encaisseLe ?? avant.encaisseLe,
      saisie,
      montantCentimes,
      tauxTvaPointsDeBase: c.tauxTvaPointsDeBase ?? avant.tauxTvaPointsDeBase,
      service,
      payeur: c.payeur !== undefined ? c.payeur : avant.payeur,
      galerieId: c.galerieId !== undefined ? c.galerieId : avant.galerieId,
      libelle: c.libelle !== undefined ? c.libelle : avant.libelle,
    });
    moisOuverts(courant, [avant.mois, apres.mois]);
    courant.encaissements = courant.encaissements.map((e) =>
      e.id === id ? { ...apres, createdAt: avant.createdAt } : e,
    );
    return apres;
  },

  supprimerEncaissement(id: string): void {
    const courant = lEtat();
    const avant = courant.encaissements.find((e) => e.id === id);
    if (!avant) throw new ApiError(404, 'ressource.introuvable', 'Encaissement introuvable.');
    moisOuverts(courant, [avant.mois]);
    courant.encaissements = courant.encaissements.filter((e) => e.id !== id);
  },
};
